import { colorScalePrimary } from "../theming/palette";

const plottingBasics = {
    height: 400,
    width: 900,
    marginLeft: 80,
    marginRight: 40,
    marginBottom: 80,
    marginTop: 100,
}

export const getBoxplotAnalyze = (plotData, t) => {
    const { runData } = plotData;
    const { predictionDetails } = runData;
    const targetIdx = plotData.selectedTargetIndex ?? 0;
    const safeTargetIdx = targetIdx < 0 ? 0 : targetIdx;

    let yData;
    if (predictionDetails.experimentType === "Forecast") {
        yData = [
            predictionDetails.CRPSListTrain || [],
            predictionDetails.CRPSListVal || [],
        ];
    } else {
        yData = [
            (predictionDetails.AEListTrain || []).map(d => d[safeTargetIdx] ?? 0),
            (predictionDetails.AEListVal || []).map(d => d[safeTargetIdx] ?? 0),
            (predictionDetails.AEListTest || []).map(d => d[safeTargetIdx] ?? 0),
        ];
    }

    const dataBoxPlot = [
        t("analyzer.TrainingSet"),
        t("analyzer.ValidationSet"),
        t("analyzer.TestSet")
    ].map((name, index) => ({
        type: "box",
        y: yData[index],
        name,
        boxpoints: "all",
        marker: { size: 3 },
    }));

    const layoutBoxPlot = {
        ...plottingBasics,
        title: predictionDetails.experimentType === "Forecast"
            ? t("analyzer.LabelCRPS")
            : t("analyzer.LabelAbsolutError"),
        showlegend: false,
    };

    return [dataBoxPlot, layoutBoxPlot];
};


export const getScatterplotAnalyze = (plotData, t) => {
    const { runData } = plotData;
    const { predictionDetails } = runData;
    const targetIdx = plotData.selectedTargetIndex ?? 0;
    const safeTargetIdx = targetIdx < 0 ? 0 : targetIdx;

    const trainX = (predictionDetails.targetListTrain || []).map(d => d[safeTargetIdx] ?? 0);
    const valX = (predictionDetails.targetListVal || []).map(d => d[safeTargetIdx] ?? 0);
    const testX =(predictionDetails.targetListTest || []).map(d => d[safeTargetIdx] ?? 0);
    const trainY = (predictionDetails.predictionsTrain || []).map(d => d[safeTargetIdx] ?? 0);
    const valY = (predictionDetails.predictionsVal || []).map(d => d[safeTargetIdx] ?? 0);
    const testY= (predictionDetails.predictionsTest || []).map(d => d[safeTargetIdx] ?? 0);

    const allX = [...trainX, ...valX, ...testX];
    const allY = [...trainY, ...valY, ...testY];

    const minVal = Math.min(...allX, ...allY);
    const maxVal = Math.max(...allX, ...allY);

    const trace1 = {
        x: trainX,
        y: trainY,
        mode: "markers",
        type: "scatter",
        name: t("analyzer.LabelTrainingSetPredictions"),
    };

    const trace2 = {
        x: valX,
        y: valY,
        mode: "markers",
        type: "scatter",
        name: t("analyzer.LabelValidationSetPredictions"),
    };
    const trace3 = {
        x: testX,
        y: testY,
        mode: "markers",
        type: "scatter",
        name: t("analyzer.LabelTestingSetPredictions"),
    };

    const equalityLineTrace = {
        x: [minVal, maxVal],
        y: [minVal, maxVal],
        mode: "lines",
        type: "scatter",
        name: t("analyzer.LabelDottedLine"),
        line: { color: "red", dash: "dashdot", width: 2 },
        hoverinfo: "skip",
    };

    const layoutScatterPlot = {
        ...plottingBasics,
        title: t("analyzer.LabelScatterPlot"),
        xaxis: { title: t("analyzer.Actual"), range: [minVal, maxVal] },
        yaxis: { title: t("analyzer.Predicted"), range: [minVal, maxVal] },
        showlegend: true,
    };

    return [[trace1, trace2,trace3, equalityLineTrace], layoutScatterPlot];
};

  

export const getFeatureImportancePlot = (plotData, t, theme) => {
    const { runData } = plotData;
    const currentTarget = 0;

    const dataHistogram = [
        {
            x: Object.values(runData.featureImportance[currentTarget]),
            y: Object.keys(runData.featureImportance[currentTarget]),
            type: "bar",
            mode: "lines+markers",
            orientation: "h",
            marker: { color: theme.palette.primary.main },
        },
    ];

    const layoutHistogram = {
        ...plottingBasics,
        width: "100%",
        height: Object.keys(runData.featureImportance[currentTarget]).length * 35,
        xaxis: { title: "Importance" },
        yaxis: {
            categoryorder: 'total ascending',
            ticksuffix: " "
        },
        margin: { l: 250, r: -250 },
        title: t("analyzer.LabelFeatureImportancePlot"),
    };

    return [dataHistogram, layoutHistogram];
};

export const getDataBalancerPlot = (plotData, t, theme) => {
    const dataBalancer = plotData.runData.dataBalancer || {};
    const binValue = dataBalancer.binValue || [];
    const original = dataBalancer.original || [];
    const balanced = dataBalancer.balanced || [];
    const {meanFrequency} = dataBalancer;
    const {scalingTarget} = dataBalancer;

    if (!binValue.length) {
        return [[], {}];
    }

    // bins overshoot the min and max of the dataset, this corrects the lenghts of the mean and scaling for this effect
    const minX = Math.min(...binValue)-(Math.max(...binValue)-Math.min(...binValue))/original.length/2;
    const maxX = Math.max(...binValue)+(Math.max(...binValue)-Math.min(...binValue))/original.length/2;

    const dataBalancerPlot = [
        {
            x: binValue,
            y: original,
            type: "bar",
            name: "original",
            marker: { color: "deeppink" },
            opacity: 0.6,
        },
        {
            x: binValue,
            y: balanced,
            type: "bar",
            name: "balanced",
            marker: { color: "darkgreen" },
            opacity: 0.35,
        },
        {
            x: [minX, maxX],
            y: [meanFrequency, meanFrequency],
            type: "scatter",
            mode: "lines",
            name: "mean frequency",
            line: { color: theme.palette.warning.main, dash: "dash" },
            hoverinfo: "skip",
            visible: meanFrequency !== undefined ? true : "legendonly",
        },
        {
            x: [minX, maxX],
            y: [scalingTarget, scalingTarget],
            type: "scatter",
            mode: "lines",
            name: "scaling target",
            line: { color: "blue", dash: "dashdot" },
            hoverinfo: "skip",
            visible: scalingTarget !== undefined ? true : "legendonly",
        },
    ];

    const layoutDataBalancerPlot = {
        ...plottingBasics,
        title: t("analyzer.DataBalancingPlotTitle") ,
        xaxis: { title: t("analyzer.DataBalancingPlotXTitle") } ,
        yaxis: { title: t("analyzer.DataBalancingPlotYTitle") },
        showlegend: true,
        barmode: "overlay",
        bargap: 0,
        bargroupgap: 0,
    };

    return [dataBalancerPlot, layoutDataBalancerPlot];
};


export const getConfusionMatrix = (plotData, t) => {

    const dataConfusionMatrix = [
        {
            z: plotData.runData.predictionDetails[plotData.type],
            x: plotData.runData.predictionDetails.classNames,
            y: plotData.runData.predictionDetails.classNames,
            type: "heatmap",
            colorscale: colorScalePrimary,
            text: plotData.runData.predictionDetails[plotData.type].map((row) =>
                row.map((value) => String(value))
            ),
            hoverinfo: "text",
            reversescale: true,
            colorbar: { title: t("analyzer.ConfusionMatrixColorBarLabel") },
        },
    ];

    // update layout
    const layoutConfusionMatrix = {
        ...plottingBasics,
        marginLeft: 140,
        title: t(`analyzer.${plotData.type.charAt(0).toUpperCase() + plotData.type.slice(1)}Title`),
        annotations: [],
        xaxis: { title: t("analyzer.ConfusionMatrixXTitle") },
        yaxis: { title: t("analyzer.ConfusionMatrixYTitle") },
        textAuto: true,
    };

    // Add annotations to the layout
    for (let i = 0; i < dataConfusionMatrix[0].y.length; i++) {
        for (let j = 0; j < dataConfusionMatrix[0].x.length; j++) {
            const currentValue = dataConfusionMatrix[0].z[i][j];
            const textColor = currentValue > 40 ? "#ffffff" : "#000000"; // Adjust the text color based on the cell value
            const annotation = {
                xref: "x1",
                yref: "y1",
                x: dataConfusionMatrix[0].x[j],
                y: dataConfusionMatrix[0].y[i],
                text: currentValue.toFixed(2),
                font: {
                    color: textColor,
                },
                showarrow: false,
            };
            layoutConfusionMatrix.annotations.push(annotation);
        }
    }

    return [dataConfusionMatrix, layoutConfusionMatrix]
}

export const getScatterplotTime = (plotData, t, theme) => {
    const { currentTarget, timestamps, targetSeries } = plotData
    const x = Array.isArray(timestamps) && timestamps.length > 0 ? timestamps : [];
    const y = Array.isArray(targetSeries) && targetSeries.length > 0 ? targetSeries : [];

    if (x.length !== y.length) {
      console.error("Mismatched x and y lengths:", x.length, y.length);
      return [[], {}];
    }

    if (x.length === 0 || y.length === 0) {
      // No data to plot
      return [[], {}];
    }

    const dataTimeSeries = [
      {
        x,
        y,
        type: "scatter",
        mode: "lines+markers",
        marker: { color: theme.palette.secondary.dark },
      },
    ];

    const layoutTimeSeries = {
      ...plottingBasics,
      width: "100%",
      height: "auto",
      title: t("inspect.TargetOverTime"),
      xaxis: {
        title: "Time",
        tickformat: "%b\n%Y",
        tickmode: "auto",
        nticks: 10,
      },
      yaxis: {
        title: `${currentTarget}`,
      },
    };

    return [dataTimeSeries, layoutTimeSeries];
};

export const getScatterplotAFS = (plotData, t, theme) => {

    const dataLossAndFeatures =
        [
            {
                x: plotData.runData.epochs,
                y: plotData.runData.progressValRMSE,
                type: "scatter",
                mode: "markers",
                marker: { color: theme.palette.primary.light, line: { width: 0.5 } },
                name: t("AFSTrainingStatus.ScatterYAxis1Label"),
            },
            {
                x: plotData.runData.epochs,
                y: plotData.runData.progressNumFeatures,
                type: "scatter",
                mode: "lines+markers",
                marker: { color: theme.palette.primary.dark },
                yaxis: 'y2',
                name: t("AFSTrainingStatus.ScatterYAxis2Label"),
            },
            {
                x: [plotData.runData.finalRobustX, plotData.runData.finalIntersectX],
                y: [plotData.runData.finalRobustY, plotData.runData.finalIntersectY],
                type: "scatter",
                mode: "lines",


                yaxis: "y"
            }

        ]

    const layoutScatter =
    {
        ...plottingBasics,
        width: "100%",
        height: "auto",
        title: t("AFSTrainingStatus.ScatterTitle"),
        xaxis: {
            title: t("AFSTrainingStatus.ScatterXAxisLabel"),
            tickformat: "%b\n%Y",
            tickmode: "auto",
            nticks: 10,
            showgrid: true
        },
        yaxis: {
            title: t("AFSTrainingStatus.ScatterYAxis1Label"),
            showgrid: true
        },
        yaxis2: {
            title: t("AFSTrainingStatus.ScatterYAxis2Label"),
            overlaying: 'y',
            side: 'right',
            showgrid: false
        },
        // set the legend to the top right
        legend: {
            orientation: "h",
            yanchor: "bottom",
            y: 1.02,
            xanchor: "right",
            x: 1
        },
        // check if final data is available, if so add annotation
        annotations: (plotData.runData.finalDoubleLinFit ? [
            {
                // intersection point
                x: plotData.runData.finalIntersectX,
                y: plotData.runData.finalIntersectY,
                xref: 'x',
                yref: 'y',
                text: t("AFSTrainingStatus.ScatterIntersectLabel"),
                showarrow: true,
                arrowhead: 6,
                arrowcolor: theme.palette.tertiary.dark,
                borderpad: 1,
                bgcolor: "rgba(255, 255, 255, 0.5)",
                ax: 20,
                ay: -30
            },
            {
                // robust point
                x: plotData.runData.finalRobustX,
                y: plotData.runData.finalRobustY,
                xref: 'x',
                yref: 'y',
                text: t("AFSTrainingStatus.ScatterRobustLabel"),
                showarrow: true,
                arrowhead: 6,
                arrowcolor: theme.palette.success.main,
                borderpad: 1,
                bgcolor: "rgba(255, 255, 255, 0.5)",
                ax: -20,
                ay: -30
            },
        ] : [])
    }
    return [dataLossAndFeatures, layoutScatter]
}



export function getAFSBarChart(plotData, t) {
    const features = plotData.runData.y;
    const numFeatures = features.length;
    const heightPerFeature = 20;
    const dynamicHeight = Math.max(numFeatures * heightPerFeature, 400);


    // Handle features that were not selected (ensure 0s exist)
    const selectionCounts = plotData.runData.z.map(row => row.reduce((a, b) => a + b, 0));

    const dataBarChart = [
        {
            x: selectionCounts,
            y: features,
            type: "bar",
            orientation: "h",
            name: t("AFSTrainingStatus.HeatmapColorMax"),
        }
    ];

    // Add vertical line at finalRobustX
    if (plotData.runData.finalDoubleLinFit) {
        const robustX = plotData.runData.finalRobustX;
        const yStart = features[0];
        const yEnd = features[features.length - 1];

        dataBarChart.push({
            x: [robustX, robustX],
            y: [yStart, yEnd],
            type: "scatter",
            mode: "lines",
            name: t("AFSTrainingStatus.ScatterRobustLabel"),
            showlegend: true,
            hoverinfo: "skip"
        });
    }

    const layoutBarChart = {
        title: t("AFSTrainingStatus.HeatmapTitle"),
        height: dynamicHeight,
        width: 900,
        margin: {
            l: 250,
            r: 50,
            t: 60,
            b: 80
        },
        xaxis: {
            title: t("AFSTrainingStatus.HeatmapXTitle"),
        },
        yaxis: {
            title: t("AFSTrainingStatus.HeatmapYTitle"),
            automargin: true,
            type: "category",
            tickfont: { size: 12 },
            fixedrange: true,
            range: [features.length - 0.5, -0.5]  // Prevent padding at top/bottom
        },
        showlegend: true,
        annotations: plotData.runData.finalDoubleLinFit ? [
            {
                x: plotData.runData.finalRobustX,
                y: plotData.runData.y[0],
                xref: "x",
                yref: "y",
                text: t("AFSTrainingStatus.ScatterRobustLabel"),
                showarrow: true,
                arrowhead: 2,
                ax: 20,
                ay: -30,
                bgcolor: "rgba(255,255,255,0.8)"
            }
        ] : []
    };
    return [dataBarChart, layoutBarChart];
}


export const getHistogramplot = (plotData, t, theme) => {

    const { currentTarget, runData } = plotData
    const dataHistogram = [
        {
            x: runData.infoPlotsData[currentTarget],
            type: "histogram",
            mode: "lines+markers",
            marker: { color: theme.palette.secondary.dark },
        },
    ]
    const layoutHistogram = {
        ...plottingBasics,
        width: "100%",
        height: "auto",
        xaxis: {
            title: `${currentTarget}`,
        },
        yaxis: {
            title: "Count",
        },
        title: t("inspect.TargetDistribution"),
    }
    return [dataHistogram, layoutHistogram]
}
export const getPGPEScatterPlotLayout = (key, maxEpochs, t) => {
    // Replace underscores with spaces and convert to uppercase
    const readableHyperparameter = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    maxEpochs = parseInt(maxEpochs, 10)
    return {
        ...plottingBasics,
        autosize: true,
        hovermode: 'closest',
        height: 400,
        width: undefined,
        title: readableHyperparameter,
        xaxis: { title: t("trainingStatus.OptimizerEpoch"), range: (maxEpochs > 1 ? [0, maxEpochs + 1] : 1) },
        yaxis: { "title": readableHyperparameter },
        legend: {
            orientation: "v",
            x: 1,
            xanchor: "left",
            y: 1,
            yanchor: "top",
            bgcolor: "rgba(255,255,255,0.7)",
        },
        margin: { l: 70, r: 30, t: 70, b: 70 },
        colorscale: "Aggrnyl"
    }

}

export const getPGPEMetricPlotLayout = (maxEpochs, t) => ({
    ...plottingBasics,
    autosize: true,
    hovermode: 'closest',
    height: 400,
    width: undefined,
    title: t("trainingStatus.Metric"),
    xaxis: {
        title: t("trainingStatus.OptimizerEpoch"),
        range: (parseInt(maxEpochs, 10) > 1 ? [0, parseInt(maxEpochs, 10) + 1] : 1)
    },
    yaxis: { "title": t("trainingStatus.Metric"), },
    legend: {
        orientation: "v",
        x: 1,
        xanchor: "left",
        y: 1,
        yanchor: "top",
        bgcolor: "rgba(255,255,255,0.7)",
    },
    margin: { l: 70, r: 30, t: 80, b: 70 },
    colorscale: "Aggrnyl"
})
export const getPGPEParcoordPlotLayout = (t) => ({
    ...plottingBasics,
    autosize: true,
    hovermode: 'closest',
    height: 400,
    width: undefined,
    title: t("trainingStatus.Parcoord"),
    margin: { l: 60, r: 110, t: 70, b: 60 },
    colorscale: "Aggrnyl"
})



export const getErrorModelPlot = (plotData, t) => {
    if (!plotData?.runData?.predictionDetails) {
      return [[], {}]; // or return null if the component handles it
    }

    const {
      timestamps,
      predictions,
      groundTruth,
      min3Sigma,
      max3Sigma,
      min4Sigma,
      max4Sigma,
      warnings,
      errors,
      splitMask
    } = plotData.runData.predictionDetails;

    // create masked predictions for train and val
    const predictionsTrain = predictions.map((value, i) =>
          splitMask[i] === 1 ? null : value
        );
    const predictionsVal = predictions.map((value, i) =>
          splitMask[i] === 0 ? null : value
        );

      const data = [
        {
          x: [...timestamps, ...timestamps.slice().reverse()],
          y: [...max4Sigma, ...min4Sigma.slice().reverse()],
          fill: "toself",
          fillcolor: "#E8E8FD",
          line: { color: "transparent" },
          type: "scatter",
          mode: "lines",
          name: "±4σ",
          hoverinfo: "skip",
        },
        {
          x: [...timestamps, ...timestamps.slice().reverse()],
          y: [...max3Sigma, ...min3Sigma.slice().reverse()],
          fill: "toself",
          fillcolor: "#C4C3F7",
          line: { color: "transparent" },
          type: "scatter",
          mode: "lines",
          name: "±3σ",
          hoverinfo: "skip",
        },
        {
          x: timestamps,
          y: groundTruth,
          type: "scatter",
          mode: "lines",
          name: "Ground Truth",
          line: { color: "black" },
        },
        {
          x: timestamps,
          y: predictionsVal,
          type: "scatter",
          mode: "lines",
          connectGaps: false,
          name: "Prediction (Validation)",
          line: { color: "blue"  },
        },
          {
          x: timestamps,
          y: predictionsTrain,
          type: "scatter",
          mode: "lines",
          connectGaps: false,
          name: "Prediction (Training)",
          line: { color: "#ee16c2"  },
        },
      ];

      // Add warning and error points
      if (warnings?.length > 0) {
        // only include timestamps where warning is 1
        const warningTimestamps = timestamps.filter((_, i) => warnings[i] === 1);
        // use prediction as y value for warnings
        const warningPositions = groundTruth.filter((_, i) => warnings[i] === 1);
        data.push({
          x: warningTimestamps,
          y: warningPositions,
          type: "scatter",
          mode: "markers",
          name: "Warning",
          marker: { color: "orange", size: 10 },
          text: Array(warnings.length).fill("Warning"),
          textposition: "top center",
          showlegend: true,
        });
      }

      if (errors?.length > 0) {
      // only include timestamps where error is 1
        const errorTimestamps = timestamps.filter((_, i) => errors[i] === 1);
        // use prediction as y value for errors
        const errorPositions = groundTruth.filter((_, i) => errors[i] === 1);
        data.push({
          x: errorTimestamps,
          y: errorPositions,
          type: "scatter",
          mode: "markers",
          name: "Outlier",
          marker: { color: "red", size: 10 },
          text: Array(errors.length).fill("Error"),
          textposition: "top center",
          showlegend: true,
        });
      }

      const layout = {
        title: t("analyzer.LabelErrorModelPlot"),
        xaxis: {
          title: "Time",
          type: "date",
          rangeslider: { visible: true },
        },
        yaxis: {
          title: "Value",
        },
        legend: {
          orientation: "v",
          y: -0.2,
        },
        margin: {
          t: 40,
          l: 60,
          r: 30,
          b: 60,
        },
        height: 500,
        template: "plotly_white",
      };

      return [data, layout];

    }

export const getErrorModelScatterPlot = (plotData, t) => {
    // const { runData } = plotData;
    // const { predictionDetails } = runData;
    // const targetIdx = plotData.selectedTargetIndex ?? 0;
    // const safeTargetIdx = targetIdx < 0 ? 0 : targetIdx;
    const {
    //   timestamps,
      predictions,
      groundTruth,
    //   min3Sigma,
    //   max3Sigma,
    //   min4Sigma,
    //   max4Sigma,
      warnings,
      errors,
      splitMask
    } = plotData.runData.predictionDetails;
        // create masked predictions for train and val
    const trainX = groundTruth.map((value, i) =>
          splitMask[i] === 1 ? null : value
    );
    const trainY = predictions.map((value, i) =>
          splitMask[i] === 1 ? null : value
        );
    const valX = groundTruth.map((value, i) =>
          splitMask[i] === 0 ? null : value
        );
    const valY = predictions.map((value, i) =>
          splitMask[i] === 0 ? null : value
        );

    const allX = [...trainX, ...valX];
    const allY = [...trainY, ...valY];

    const minVal = Math.min(...allX, ...allY);
    const maxVal = Math.max(...allX, ...allY);

    const trace1 = {
        x: trainX,
        y: trainY,
        mode: "markers",
        type: "scatter",
        marker: { color: "#ee16c2"  },
        name: t("analyzer.LabelTrainingSetPredictions"),
    };

    const trace2 = {
        x: valX,
        y: valY,
        mode: "markers",
        type: "scatter",
        marker: { color: "blue"  },
        name: t("analyzer.LabelValidationSetPredictions"),
    };

    // Add warning and error points
    // only include prediction where warning is 1
    const warningX = groundTruth.filter((_, i) => warnings[i] === 1);
    // use target as y value for warnings
    const warningY = predictions.filter((_, i) => warnings[i] === 1);
    const trace3 = {
      x: warningX,
      y: warningY,
      type: "scatter",
      mode: "markers",
      name: "Warning",
      marker: { color: "orange", size: 10 },
      text: Array(warnings.length).fill("Warning"),
      textposition: "top center",
      showlegend: true,
    }

    // only include predictions where error is 1
    const errorX = groundTruth.filter((_, i) => errors[i] === 1);
    // use target as y value for errors
    const errorY = predictions.filter((_, i) => errors[i] === 1);
    const trace4 = {
      x: errorX,
      y: errorY,
      type: "scatter",
      mode: "markers",
      name: "Outlier",
      marker: { color: "red", size: 10 },
      text: Array(errors.length).fill("Error"),
      textposition: "top center",
      showlegend: true,
    }


    const equalityLineTrace = {
        x: [minVal, maxVal],
        y: [minVal, maxVal],
        mode: "lines",
        type: "scatter",
        name: t("analyzer.LabelDottedLine"),
        line: { color: "black", dash: "dashdot", width: 2 },
        hoverinfo: "skip",
    };

    const layoutScatterPlot = {
        ...plottingBasics,
        title: t("analyzer.LabelScatterPlot"),
        xaxis: { title: t("analyzer.Actual"), range: [minVal, maxVal] },
        yaxis: { title: t("analyzer.Predicted"), range: [minVal, maxVal] },
        showlegend: true,
    };

    return [[trace1, trace2, trace3, trace4, equalityLineTrace], layoutScatterPlot];
};
